import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  TextField,
  Checkbox,
  ListItemText,
  FormControl,
  FormControlLabel,
  Switch,
  Divider,
  Chip,
  Tooltip
} from '@mui/material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useState } from 'react';
import facilityOptions from './constants/facilityOptions';
import LoadingPlaceholder from '../ui/Loading/LoadingPlaceholder';
import { hasFeature } from '../../permissions';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:7071';
const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const createEmptyScheduleEntry = (overrides = {}) => ({
  label: '',
  appliesTo: '',
  days: [],
  start: '',
  end: '',
  note: '',
  ...overrides
});

const normalizeScheduleForForm = (schedule) => {
  if (!schedule || typeof schedule !== 'object') {
    return { enabled: false, entries: [], notes: '' };
  }

  return {
    enabled: Boolean(schedule.enabled) && ((Array.isArray(schedule.entries) && schedule.entries.length > 0) || Boolean(schedule.notes)),
    entries: Array.isArray(schedule.entries)
      ? schedule.entries.map(entry => ({
          ...createEmptyScheduleEntry(),
          ...entry,
          days: Array.isArray(entry?.days) ? entry.days : []
        }))
      : [],
    notes: typeof schedule.notes === 'string' ? schedule.notes : ''
  };
};

const defaultFacilityForm = {
  category: '',
  items: [],
  customItems: [],
  schedule: {
    enabled: false,
    entries: [],
    notes: ''
  }
};

export default function FacilitySection({
  entries,
  setEntries,
  form,
  setForm,
  open,
  setOpen,
  handleEdit,
  handleDelete,
  propertyId,
  allowedFeatures,
  featureScope,
  sectionType,
  featureKeyMap,
  selectedFacility,
  setSelectedFacility,
  facilityDetailsOpen,
  setFacilityDetailsOpen,
  fetchEntries
}) {
  const [loading, setLoading] = useState(false);

  const getLocalAF = () => {
    try {
      return JSON.parse(localStorage.getItem('allowedFeatures') || '{}');
    } catch (e) {
      console.warn('[FacilitySection] Failed to parse allowedFeatures from localStorage:', e);
      return {};
    }
  };

  const AF = allowedFeatures || getLocalAF();
  const canAdd = !!(AF?.facilities?.addFacility ?? hasFeature('facilities.addFacility'));
  const canEdit = !!(AF?.facilities?.editFacility ?? hasFeature('facilities.editFacility'));
  const canDelete = !!(AF?.facilities?.deleteFacility ?? hasFeature('facilities.deleteFacility'));

  useEffect(() => {
    console.log('[FacilitySection] allowedFeatures (prop):', allowedFeatures);
    console.log('[FacilitySection] allowedFeatures (localStorage):', getLocalAF());
    console.log('[FacilitySection] AF (effective):', AF);
    console.log('[FacilitySection] permissions ->', { canAdd, canEdit, canDelete, facilities: AF?.facilities });
  }, [allowedFeatures, canAdd, canEdit, canDelete, AF]);

  const refetch = async () => {
    try {
      setLoading(true);
      await fetchEntries();
    } finally {
      setLoading(false);
    }
  };

  const updateFacilityOrder = async (reorderedList) => {
    const sortedList = reorderedList.map((entry, index) => ({ ...entry, priority: index }));
    setEntries(sortedList);
    await fetch(`${API_BASE}/api/facilities/reorder`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        propertyId,
        facilities: sortedList.map(f => ({ _id: f._id, priority: f.priority }))
      })
    });
    await refetch();
  };

  const handleCategoryChange = (category) => {
    setForm(prev => ({
      ...defaultFacilityForm,
      schedule: prev?.schedule?.enabled ? prev.schedule : defaultFacilityForm.schedule,
      category
    }));
  };

  const formSchedule = form.schedule || defaultFacilityForm.schedule;
  const combinedItems = [
    ...(form.items || []),
    ...(form.customItems || []).filter(item => item && item.trim().length > 0)
  ];

  const updateSchedule = (updater) => {
    setForm(prev => {
      const current = prev.schedule || { ...defaultFacilityForm.schedule };
      const next = typeof updater === 'function' ? updater(current) : updater;
      return { ...prev, schedule: next };
    });
  };

  const handleScheduleEntryChange = (index, key, value) => {
    updateSchedule(schedule => {
      const entries = schedule.entries ? [...schedule.entries] : [];
      entries[index] = { ...entries[index], [key]: value };
      return { ...schedule, entries };
    });
  };

  const handleToggleDay = (index, day) => {
    updateSchedule(schedule => {
      const entries = schedule.entries ? [...schedule.entries] : [];
      const entry = entries[index] || createEmptyScheduleEntry();
      const days = new Set(entry.days || []);
      if (days.has(day)) {
        days.delete(day);
      } else {
        days.add(day);
      }
      entries[index] = { ...entry, days: Array.from(days).sort((a, b) => DAYS_OF_WEEK.indexOf(a) - DAYS_OF_WEEK.indexOf(b)) };
      return { ...schedule, entries };
    });
  };

  const handleRemoveScheduleEntry = (index) => {
    updateSchedule(schedule => {
      const entries = schedule.entries ? schedule.entries.filter((_, idx) => idx !== index) : [];
      return { ...schedule, entries };
    });
  };

  const handleAddScheduleEntry = () => {
    updateSchedule(schedule => ({
      ...schedule,
      entries: [...(schedule.entries || []), createEmptyScheduleEntry({ appliesTo: combinedItems[0] || '' })]
    }));
  };

  useEffect(() => {
    if (!open) return;
    setForm(prev => {
      if (!prev || typeof prev !== 'object') return prev;
      if (!prev.category) return prev;

      const options = facilityOptions?.[prev.category] || [];
      const rawItems = Array.isArray(prev.items) ? prev.items.filter(Boolean) : [];
      const predefinedItems = rawItems.filter(item => options.includes(item));
      const derivedCustomItems = prev.customItems
        ? prev.customItems
        : rawItems.filter(item => !options.includes(item));
      const normalizedSchedule = normalizeScheduleForForm(prev.schedule);

      const itemsChanged = JSON.stringify(predefinedItems) !== JSON.stringify(prev.items || []);
      const customChanged = prev.customItems
        ? false
        : derivedCustomItems.length > 0;
      const scheduleMissing = !prev.schedule;
      const scheduleChanged =
        scheduleMissing ||
        JSON.stringify(normalizedSchedule) !== JSON.stringify(prev.schedule || defaultFacilityForm.schedule);

      if (!itemsChanged && !customChanged && !scheduleChanged) {
        return prev;
      }

      return {
        ...prev,
        items: predefinedItems,
        customItems: derivedCustomItems,
        schedule: normalizedSchedule
      };
    });
  }, [open, setForm]);

  return (
    <Box sx={{ px: 3 }}>
      <>
      {canAdd && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="contained" onClick={() => {
            console.log('[FacilitySection] Add Facility clicked, canAdd=', canAdd);
            setForm({ ...defaultFacilityForm });
            setOpen(true);
          }}>
            + Add Facility
          </Button>
        </Box>
      )}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth>
        <DialogTitle>{form._id ? 'Edit' : 'Add'} Facility</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Category</InputLabel>
            <Select
              value={form.category || ''}
              onChange={e => handleCategoryChange(e.target.value)}
              label="Category"
            >
              {Object.keys(facilityOptions).map(cat => (
                <MenuItem key={cat} value={cat}>{cat}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {form.category && (
            <FormControl fullWidth margin="normal">
              <InputLabel>Items</InputLabel>
              <Select
                multiple
                value={form.items || []}
                onChange={e => setForm({ ...form, items: e.target.value })}
                renderValue={(selected) => selected.join(', ')}
              >
                {(facilityOptions[form.category] || []).map(item => (
                  <MenuItem key={item} value={item}>
                    <Checkbox checked={form.items?.includes(item)} />
                    <ListItemText primary={item} />
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="subtitle2" sx={{ mt: 2 }}>Add Custom Facility Items</Typography>
              {(form.customItems || ['']).map((item, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, my: 1 }}>
                  <TextField
                    fullWidth
                    label={`Custom Item ${index + 1}`}
                    value={item}
                    onChange={e => {
                      const updated = [...(form.customItems || [])];
                      updated[index] = e.target.value;
                      setForm(prev => ({ ...prev, customItems: updated }));
                    }}
                  />
                  <IconButton onClick={() => {
                    const updated = (form.customItems || []).filter((_, i) => i !== index);
                    setForm(prev => ({ ...prev, customItems: updated }));
                  }}>
                    <CloseIcon />
                  </IconButton>
                </Box>
              ))}
              <Button onClick={() => setForm(prev => ({ ...prev, customItems: [...(prev.customItems || []), ''] }))}>
                + Add Custom Item
              </Button>
            </FormControl>
          )}
          {form.category && (
            <>
              <Divider sx={{ my: 3 }} />
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(formSchedule.enabled)}
                    onChange={(_, checked) => {
                      if (!checked) {
                        updateSchedule({ ...defaultFacilityForm.schedule });
                      } else {
                        updateSchedule({ ...defaultFacilityForm.schedule, enabled: true });
                        if (!(formSchedule.entries && formSchedule.entries.length)) {
                          handleAddScheduleEntry();
                        }
                      }
                    }}
                  />
                }
                label="Add operating hours"
              />

              {formSchedule.enabled && (
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {(formSchedule.entries || []).map((entry, index) => (
                    <Box
                      key={`schedule-entry-${index}`}
                      sx={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 2,
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        backgroundColor: '#fafafa'
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                        <TextField
                          fullWidth
                          label="Service Label"
                          placeholder="Shuttle Service"
                          value={entry.label || ''}
                          onChange={(e) => handleScheduleEntryChange(index, 'label', e.target.value)}
                        />
                        <FormControl fullWidth>
                          <InputLabel>Applies To</InputLabel>
                          <Select
                            value={entry.appliesTo || ''}
                            label="Applies To"
                            onChange={(e) => handleScheduleEntryChange(index, 'appliesTo', e.target.value)}
                          >
                            <MenuItem value="">
                              <em>All facility items</em>
                            </MenuItem>
                            {combinedItems.map(item => (
                              <MenuItem key={item} value={item}>{item}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>

                      <FormControl fullWidth>
                        <InputLabel>Days</InputLabel>
                        <Select
                          multiple
                          value={entry.days || []}
                          label="Days"
                          renderValue={(selected) => selected.join(', ')}
                          onChange={(e) => handleScheduleEntryChange(index, 'days', e.target.value)}
                        >
                          {DAYS_OF_WEEK.map(day => (
                            <MenuItem
                              key={day}
                              value={day}
                              onClick={(event) => {
                                // Prevent MUI from toggling all selections at once while we manage toggling manually.
                                event.preventDefault();
                                event.stopPropagation();
                                handleToggleDay(index, day);
                              }}
                            >
                              <Checkbox checked={entry.days?.includes(day)} />
                              <ListItemText primary={day} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                        <TextField
                          fullWidth
                          label="Opens"
                          type="time"
                          value={entry.start || ''}
                          onChange={(e) => handleScheduleEntryChange(index, 'start', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                          fullWidth
                          label="Closes"
                          type="time"
                          value={entry.end || ''}
                          onChange={(e) => handleScheduleEntryChange(index, 'end', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Box>

                      <TextField
                        fullWidth
                        label="Notes"
                        placeholder="Last departure at 10:30 PM"
                        value={entry.note || ''}
                        onChange={(e) => handleScheduleEntryChange(index, 'note', e.target.value)}
                        multiline
                        minRows={2}
                      />

                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Entry {index + 1}
                        </Typography>
                        <Button color="error" onClick={() => handleRemoveScheduleEntry(index)}>
                          Remove Entry
                        </Button>
                      </Box>
                    </Box>
                  ))}

                  <Button variant="outlined" onClick={handleAddScheduleEntry}>
                    + Add Time Slot
                  </Button>

                  <TextField
                    fullWidth
                    label="Additional Notes"
                    placeholder="Guests can request the shuttle 15 minutes before departure."
                    value={formSchedule.notes || ''}
                    onChange={(e) => updateSchedule(schedule => ({ ...schedule, notes: e.target.value }))}
                    multiline
                    minRows={2}
                  />
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              console.log('[FacilitySection] Save clicked. form._id=', !!form._id, { canAdd, canEdit, AF });
              // Permission gate
              if (form._id && !canEdit) { console.warn('[FacilitySection] Blocked: edit without permission', { canEdit, AF }); alert('You do not have permission to edit facilities.'); return; }
              if (!form._id && !canAdd) { console.warn('[FacilitySection] Blocked: add without permission', { canAdd, AF }); alert('You do not have permission to add facilities.'); return; }

              // Validation
              if (!form.category) {
                alert('Please select a category.');
                return;
              }
              if (
                (!form.items || !form.items.length) &&
                !(form.customItems && form.customItems.some(item => item.trim()))
              ) {
                alert('Please add at least one facility item or custom item.');
                return;
              }
              const cleanedCustomItems = (form.customItems || []).map(item => item.trim()).filter(Boolean);
              const cleanedScheduleEntries = (formSchedule.entries || [])
                .map(entry => ({
                  label: entry.label?.trim() || '',
                  appliesTo: entry.appliesTo?.trim() || '',
                  days: Array.isArray(entry.days) ? entry.days.filter(Boolean) : [],
                  start: entry.start || '',
                  end: entry.end || '',
                  note: entry.note?.trim() || ''
                }))
                .filter(entry =>
                  entry.label ||
                  entry.appliesTo ||
                  entry.days.length ||
                  entry.start ||
                  entry.end ||
                  entry.note
                );
              const payloadSchedule = formSchedule.enabled && (cleanedScheduleEntries.length || (formSchedule.notes && formSchedule.notes.trim()))
                ? {
                    enabled: true,
                    entries: cleanedScheduleEntries,
                    notes: formSchedule.notes?.trim() || ''
                  }
                : { enabled: false, entries: [], notes: '' };
              const payload = {
                propertyId,
                category: form.category,
                items: (form.items || []).map(item => item.trim()).filter(Boolean),
                customItems: cleanedCustomItems,
                schedule: payloadSchedule
              };

              const method = form._id ? 'PUT' : 'POST';
              const url = form._id
                ? `${API_BASE}/api/facilities/${form._id}`
                : `${API_BASE}/api/facilities`;

              try {
                const response = await fetch(url, {
                  method,
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(payload)
                });

                if (response.ok) {
                  setOpen(false);
                  setForm({ ...defaultFacilityForm });
                  await refetch();
                } else {
                  const errorText = await response.text();
                  console.error('Failed to save facility:', errorText);
                  alert('Failed to save facility. Please try again.');
                }
              } catch (err) {
                console.error('Error saving facility:', err);
                alert('An unexpected error occurred. Please try again.');
              }
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={facilityDetailsOpen} onClose={() => setFacilityDetailsOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{selectedFacility?.category || 'Facility Details'}</DialogTitle>
        <DialogContent>
          {selectedFacility?.items && Array.isArray(selectedFacility.items) ? (
            selectedFacility.items.length > 0 ? (
              <ul>
                {selectedFacility.items.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            ) : (
              <p>No items listed.</p>
            )
          ) : (
            <p>Loading items...</p>
          )}
          {selectedFacility?.schedule?.enabled && (selectedFacility.schedule.entries?.length > 0 || selectedFacility.schedule.notes) && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>Operating Hours</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {selectedFacility.schedule.entries?.map((entry, idx) => (
                  <Box key={`facility-schedule-entry-${idx}`}>
                    <Typography variant="body2" color="text.secondary">
                      {(entry.label || entry.appliesTo || `Entry ${idx + 1}`)}{entry.appliesTo ? ` · ${entry.appliesTo}` : ''}
                    </Typography>
                    <Typography variant="body2">
                      {entry.days?.length ? entry.days.join(', ') : 'Daily'} · {entry.start || '—'} – {entry.end || '—'}
                    </Typography>
                    {entry.note ? (
                      <Typography variant="caption" color="text.secondary">{entry.note}</Typography>
                    ) : null}
                  </Box>
                ))}
                {selectedFacility.schedule.notes ? (
                  <Typography variant="body2">{selectedFacility.schedule.notes}</Typography>
                ) : null}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFacilityDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {loading ? (
        <Box sx={{ my: 3 }}>
          <LoadingPlaceholder />
        </Box>
      ) : null}
      {!loading && (
      <DragDropContext
        onDragEnd={(result) => {
          if (!canEdit) { console.warn('[FacilitySection] Reorder blocked by permission. canEdit=false'); return; }
          const { source, destination } = result;
          if (!destination || source.index === destination.index) return;
          const reordered = Array.from(entries.slice().sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0)));
          const [moved] = reordered.splice(source.index, 1);
          reordered.splice(destination.index, 0, moved);
          updateFacilityOrder(reordered);
        }}
      >
        <Droppable droppableId="facilities-list">
          {(provided) => (
            <Box component="table" sx={{ width: '100%', mt: 2 }} ref={provided.innerRef} {...provided.droppableProps}>
              <Box component="thead">
                <Box component="tr">
                  <Box component="th" sx={{ textAlign: 'left', paddingBottom: 1 }}>Category</Box>
                  <Box component="th" sx={{ textAlign: 'left', paddingBottom: 1 }}>Items</Box>
                  <Box component="th" sx={{ textAlign: 'left', paddingBottom: 1 }}>Operating Hours</Box>
                  <Box component="th" sx={{ textAlign: 'left', paddingBottom: 1 }}>Actions</Box>
                </Box>
              </Box>
              <Box component="tbody">
              {(entries.slice().sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))).map((entry, index) => (
                <Draggable key={entry._id} draggableId={entry._id} index={index}>
                  {(provided) => (
                    <Box
                      component="tr"
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      sx={{ display: 'table-row' }}
                    >
                      <Box component="td">{entry.category}</Box>
                      <Box component="td" sx={{ cursor: 'pointer' }} onClick={() => {
                        setSelectedFacility(entry);
                        setFacilityDetailsOpen(true);
                      }}>
                        {Array.isArray(entry.items) && entry.items.length > 0 ? entry.items.join(', ') : '-'}
                      </Box>
                      <Box component="td">
                        {entry.schedule?.enabled && entry.schedule.entries?.length ? (
                          <Tooltip title="Operating hours configured">
                            <Chip label="Has hours" color="primary" size="small" />
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </Box>
                      <Box component="td">
                        <Button size="small" onClick={() => { if (!canEdit) { console.warn('[FacilitySection] Edit blocked by permission. canEdit=false'); return; } handleEdit(entry); }} disabled={!canEdit}>Edit</Button>
                        <Button size="small" color="error" onClick={() => { if (!canDelete) { console.warn('[FacilitySection] Delete blocked by permission. canDelete=false'); return; } handleDelete(entry._id); }} disabled={!canDelete}>Delete</Button>
                      </Box>
                    </Box>
                  )}
                </Draggable>
              ))}
                {provided.placeholder}
              </Box>
            </Box>
          )}
        </Droppable>
      </DragDropContext>
      )}
      </>
    </Box>
  );
}
